/**
 * Tech-debt #4 — ChangeAvailability (OCPP 1.6J §5.2). When a target connector
 * has an active tx, the Inoperative change is deferred (csmsSession) and
 * applied on stop; otherwise it's applied immediately with a StatusNotification.
 */
import { Availability, ConnectorStatus } from '@/domain/types';
import { csmsSession } from '../csmsSessionState';
import { useStationStore } from '../useStationStore';
import type { CsmsCtx, CsmsEvent } from './context';

export function handleChangeAvailability(
  event: CsmsEvent<'ChangeAvailability'>,
  ctx: CsmsCtx,
): void {
  const { adapter, dispatcher, stationId, messageId } = ctx;
  const target = event.connectorId;
  const store = useStationStore.getState();
  const allConnectors = [...store.connectors.values()];
  const affected = target === 0 ? allConnectors : allConnectors.filter((c) => c.id === target);
  if (affected.length === 0) {
    dispatcher.reply(messageId, { status: 'Rejected' });
    return;
  }
  const targetAvail =
    event.type === 'Operative' ? Availability.Operative : Availability.Inoperative;
  const hasActiveTx = affected.some(
    (c) => c.activeTransactionId !== null && targetAvail === Availability.Inoperative,
  );
  if (hasActiveTx) {
    csmsSession.setDeferredAvailability(stationId, { type: event.type, connectorId: target });
    dispatcher.reply(messageId, { status: 'Scheduled' });
    return;
  }
  const nextConnectors = new Map(store.connectors);
  for (const c of affected) {
    nextConnectors.set(c.id, { ...c, availability: targetAvail });
  }
  useStationStore.setState({ connectors: nextConnectors });
  for (const c of affected) {
    const wireStatus =
      targetAvail === Availability.Operative
        ? ConnectorStatus.Available
        : ConnectorStatus.Unavailable;
    void adapter.statusNotification({ connectorId: c.id, status: wireStatus }).response;
  }
  dispatcher.reply(messageId, { status: 'Accepted' });
}
